import { useCallback, useEffect, useState, type FormEvent } from 'react'
import {
  AlertCircle,
  Coins,
  Edit2,
  Filter,
  Loader2,
  Plus,
  Power,
  Search,
  Trash2,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import Modal from '../components/ui/Modal'
import { useAuth } from '../contexts/AuthContext'
import { formatCents } from '../lib/money'
import {
  LegalAreaLabel,
  RateKindLabel,
  RateKindShort,
  type CreateRateInput,
  type LegalArea,
  type RateDto,
  type RateKindT,
  type RateListResponse,
} from '@toga/shared'

const KIND_OPTIONS: RateKindT[] = ['HORA', 'ACTO', 'IGUALA_MENSUAL', 'HITO']

const KIND_PILL: Record<RateKindT, string> = {
  HORA: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  ACTO: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  IGUALA_MENSUAL: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
  HITO: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
}

export default function Rates() {
  const auth = useAuth()
  const canWrite =
    auth.status === 'authenticated' &&
    (auth.user.role === 'ADMIN' || auth.user.role === 'ABOGADO')

  const [items, setItems] = useState<RateDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [kindFilter, setKindFilter] = useState<RateKindT | ''>('')
  const [activeFilter, setActiveFilter] = useState<'' | 'true' | 'false'>('true')
  const [editing, setEditing] = useState<RateDto | 'new' | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (q.trim()) params.set('q', q.trim())
      if (kindFilter) params.set('kind', kindFilter)
      if (activeFilter) params.set('active', activeFilter)
      const { items } = await api.get<RateListResponse>(`/rates?${params.toString()}`)
      setItems(items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar')
    } finally {
      setLoading(false)
    }
  }, [q, kindFilter, activeFilter])

  useEffect(() => {
    void reload()
  }, [reload])

  const onDelete = async (r: RateDto) => {
    if (!confirm(`¿Eliminar la tarifa "${r.name}"?`)) return
    try {
      await api.delete(`/rates/${r.id}`)
      await reload()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error')
    }
  }

  const onToggleActive = async (r: RateDto) => {
    try {
      await api.patch(`/rates/${r.id}`, { isActive: !r.isActive })
      await reload()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error')
    }
  }

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <Coins size={20} className="text-toga-300" />
            Tarifas
          </h1>
          <p className="text-xs t-muted">
            Catálogo de tarifas reutilizables: por hora, por acto, iguala mensual o por hito.
          </p>
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
          >
            <Plus size={14} /> Nueva tarifa
          </button>
        )}
      </header>

      {/* Filtros */}
      <div className="surface p-3 flex items-end gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            size={14}
            className="absolute left-2 top-1/2 -translate-y-1/2 t-faint pointer-events-none"
          />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre o descripción…"
            className="w-full bg-transparent border border-base rounded-lg pl-7 pr-3 py-1.5 text-sm t-primary placeholder:t-faint focus:outline-none focus:border-toga-500/50"
          />
        </div>
        <label className="text-xs t-muted flex items-center gap-2">
          <Filter size={12} className="t-faint" /> Tipo
          <select
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value as RateKindT | '')}
            className="bg-transparent border border-base rounded px-2 py-1 text-sm t-secondary focus:outline-none focus:border-toga-500/50"
          >
            <option value="">Todos</option>
            {KIND_OPTIONS.map((k) => (
              <option key={k} value={k}>
                {RateKindLabel[k]}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs t-muted flex items-center gap-2">
          Estado
          <select
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value as '' | 'true' | 'false')}
            className="bg-transparent border border-base rounded px-2 py-1 text-sm t-secondary focus:outline-none focus:border-toga-500/50"
          >
            <option value="">Todas</option>
            <option value="true">Activas</option>
            <option value="false">Inactivas</option>
          </select>
        </label>
      </div>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      <div className="surface overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-800/30 text-xs uppercase tracking-wider t-faint">
            <tr>
              <th className="px-3 py-2 text-left">Nombre</th>
              <th className="px-3 py-2 text-left">Tipo</th>
              <th className="px-3 py-2 text-left">Área</th>
              <th className="px-3 py-2 text-right">Importe</th>
              <th className="px-3 py-2 text-right">IVA</th>
              <th className="px-3 py-2 text-left">Estado</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center t-muted">
                  <Loader2 size={14} className="inline-block animate-spin" /> Cargando…
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-10 text-center">
                  <Coins size={28} className="mx-auto t-faint mb-2" />
                  <p className="t-muted text-sm">
                    {q || kindFilter
                      ? 'Sin tarifas que coincidan con el filtro.'
                      : 'Aún no hay tarifas en el catálogo.'}
                  </p>
                </td>
              </tr>
            )}
            {items.map((r) => (
              <tr
                key={r.id}
                className="border-t border-base/40 hover:bg-hover cursor-pointer"
                onClick={() => setEditing(r)}
              >
                <td className="px-3 py-2 t-primary font-medium">{r.name}</td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-flex px-1.5 py-0.5 rounded text-[10px] border ${KIND_PILL[r.kind]}`}
                  >
                    {RateKindLabel[r.kind]}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs t-secondary">
                  {r.area ? LegalAreaLabel[r.area] : <span className="t-faint">—</span>}
                </td>
                <td className="px-3 py-2 text-right t-primary font-mono">
                  {formatCents(r.amountCents)} {RateKindShort[r.kind]}
                </td>
                <td className="px-3 py-2 text-right t-secondary text-xs">
                  {(r.vatRate * 100).toFixed(0)}%
                </td>
                <td className="px-3 py-2">
                  {r.isActive ? (
                    <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] border bg-emerald-500/15 text-emerald-300 border-emerald-500/30">
                      Activa
                    </span>
                  ) : (
                    <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] border bg-slate-500/15 text-slate-400 border-slate-500/30">
                      Inactiva
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                  {canWrite && (
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => void onToggleActive(r)}
                        title={r.isActive ? 'Desactivar' : 'Activar'}
                        className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-muted hover:text-toga-300"
                      >
                        <Power size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditing(r)}
                        title="Editar"
                        className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-muted hover:text-toga-300"
                      >
                        <Edit2 size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={() => void onDelete(r)}
                        title="Eliminar"
                        className="w-7 h-7 grid place-items-center rounded hover:bg-rose-500/10 t-muted hover:text-rose-400"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <RateFormModal
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            void reload()
          }}
        />
      )}
    </div>
  )
}

interface FormProps {
  initial: RateDto | null
  onClose: () => void
  onSaved: () => void
}

const AREA_OPTIONS: LegalArea[] = [
  'CIVIL',
  'PENAL',
  'LABORAL',
  'MERCANTIL',
  'ADMINISTRATIVO',
  'FAMILIA',
  'CONCURSAL',
  'TRIBUTARIO',
  'OTRO',
]

function RateFormModal({ initial, onClose, onSaved }: FormProps) {
  const isEdit = initial !== null
  const [form, setForm] = useState<{
    name: string
    kind: RateKindT
    amountEuros: string
    vatPct: string
    area: LegalArea | ''
    description: string
    isActive: boolean
  }>({
    name: initial?.name ?? '',
    kind: initial?.kind ?? 'HORA',
    amountEuros: initial ? (initial.amountCents / 100).toFixed(2).replace('.', ',') : '',
    vatPct: initial ? (initial.vatRate * 100).toFixed(0) : '21',
    area: initial?.area ?? '',
    description: initial?.description ?? '',
    isActive: initial?.isActive ?? true,
  })
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const upd = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const eurosNumber = Number(form.amountEuros.replace(',', '.'))
      if (Number.isNaN(eurosNumber) || eurosNumber < 0) {
        setSubmitting(false)
        return setError('Importe no válido')
      }
      const vatNumber = Number(form.vatPct.replace(',', '.'))
      if (Number.isNaN(vatNumber) || vatNumber < 0 || vatNumber > 100) {
        setSubmitting(false)
        return setError('IVA no válido (0–100%)')
      }

      const payload: CreateRateInput = {
        name: form.name.trim(),
        kind: form.kind,
        amountCents: Math.round(eurosNumber * 100),
        vatRate: vatNumber / 100,
        area: form.area === '' ? null : form.area,
        description: form.description.trim() || null,
        isActive: form.isActive,
      }
      if (isEdit && initial) {
        await api.patch(`/rates/${initial.id}`, payload)
      } else {
        await api.post('/rates', payload)
      }
      onSaved()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? 'No se pudo guardar.')
      } else {
        setError('Error de red.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/60 text-slate-100 focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60'

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={isEdit ? 'Editar tarifa' : 'Nueva tarifa'}
      maxWidth="max-w-xl"
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-xs t-muted mb-1.5">Nombre</label>
          <input
            required
            value={form.name}
            onChange={(e) => upd('name', e.target.value)}
            placeholder="Hora abogado senior · Demanda civil ordinaria · Iguala despacho A…"
            className={inputCls}
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs t-muted mb-1.5">Tipo</label>
            <select
              value={form.kind}
              onChange={(e) => upd('kind', e.target.value as RateKindT)}
              className={inputCls}
            >
              {KIND_OPTIONS.map((k) => (
                <option key={k} value={k}>
                  {RateKindLabel[k]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">
              Importe ({RateKindShort[form.kind]})
            </label>
            <input
              required
              inputMode="decimal"
              value={form.amountEuros}
              onChange={(e) => upd('amountEuros', e.target.value)}
              placeholder="120,00"
              className={`${inputCls} font-mono text-right`}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">IVA (%)</label>
            <input
              required
              inputMode="decimal"
              value={form.vatPct}
              onChange={(e) => upd('vatPct', e.target.value)}
              placeholder="21"
              className={`${inputCls} font-mono text-right`}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs t-muted mb-1.5">Área (opcional)</label>
            <select
              value={form.area}
              onChange={(e) => upd('area', e.target.value as LegalArea | '')}
              className={inputCls}
            >
              <option value="">— Sin área específica —</option>
              {AREA_OPTIONS.map((a) => (
                <option key={a} value={a}>
                  {LegalAreaLabel[a]}
                </option>
              ))}
            </select>
            <p className="text-[11px] t-faint mt-1">
              Si la especificas, se sugerirá en facturas de expedientes de esa área.
            </p>
          </div>
          <div className="flex items-end pb-2">
            <label className="inline-flex items-center gap-2 text-sm t-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => upd('isActive', e.target.checked)}
                className="rounded border-slate-700 bg-slate-900"
              />
              Tarifa activa
            </label>
          </div>
        </div>

        <div>
          <label className="block text-xs t-muted mb-1.5">Descripción / notas internas</label>
          <textarea
            value={form.description}
            onChange={(e) => upd('description', e.target.value)}
            rows={3}
            placeholder="Detalles, qué incluye, condiciones…"
            className={inputCls}
          />
        </div>

        {error && (
          <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm bg-slate-800/60 hover:bg-slate-800 t-secondary"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {isEdit ? 'Guardar cambios' : 'Crear tarifa'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
